import { useState, useCallback, useEffect } from 'react';

interface UseInlineFieldEditorOptions {
  initialValue: string;
  onSave: (value: string) => Promise<void>;
  onCancel?: () => void;
}

interface UseInlineFieldEditorReturn {
  // Current state
  value: string;
  isEditing: boolean;
  isSaving: boolean;
  error: string | null;

  // Actions
  startEdit: () => void;
  cancelEdit: () => void;
  handleChange: (value: string) => void;
  handleBlur: () => void;

  // For manual save (if needed)
  save: () => Promise<void>;
}

/**
 * Generic hook for inline field editing with auto-save on blur.
 * Platform-agnostic business logic for any editable field.
 *
 * Features:
 * - Enter edit mode with startEdit()
 * - Track draft value while editing
 * - Auto-save on blur
 * - Cancel discards changes
 * - Loading and error states
 *
 * @param options - Configuration for the field editor
 * @returns Field editor state and handlers
 */
export const useInlineFieldEditor = (
  options: UseInlineFieldEditorOptions,
): UseInlineFieldEditorReturn => {
  const { initialValue, onSave, onCancel } = options;

  // State
  const [value, setValue] = useState(initialValue);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Update local value if initial value changes (e.g., from API refresh)
  useEffect(() => {
    if (!isEditing) {
      setValue(initialValue);
    }
  }, [initialValue, isEditing]);

  // Start editing
  const startEdit = useCallback(() => {
    setIsEditing(true);
    setError(null);
  }, []);

  // Cancel editing - revert to original value
  const cancelEdit = useCallback(() => {
    setValue(initialValue);
    setIsEditing(false);
    setError(null);
    onCancel?.();
  }, [initialValue, onCancel]);

  // Save the current value
  const save = useCallback(async () => {
    // Don't save if value hasn't changed
    if (value === initialValue) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      await onSave(value);
      setIsEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
      // Stay in edit mode on error so user can retry
    } finally {
      setIsSaving(false);
    }
  }, [value, initialValue, onSave]);

  // Handle blur - auto-save
  const handleBlur = useCallback(() => {
    if (isEditing && !isSaving) {
      save();
    }
  }, [isEditing, isSaving, save]);

  // Handle value change
  const handleChange = useCallback((newValue: string) => {
    setValue(newValue);
  }, []);

  return {
    value,
    isEditing,
    isSaving,
    error,
    startEdit,
    cancelEdit,
    handleChange,
    handleBlur,
    save,
  };
};
